import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { Button, ButtonLink, Spinner, type ButtonSize, type ButtonVariant } from './Button';
import { IconButton } from './IconButton';
import { Icon, type IconName } from './Icon';
import { Dialog } from './Dialog';

/**
 * One action a page or a row offers, as data rather than as a button.
 *
 * Pages used to hand PageHeader a div of five ghost buttons and register
 * rows carried two or three more each, so on a 360px phone they wrapped
 * into a stack of words beside the title. As data, the component decides
 * how to show them: inline buttons on a desk, and on a phone one visible
 * action plus a "More actions" sheet of 48px rows for the rest.
 *
 * Exactly one of `onClick`, `to` or `href` should be set.
 */
export interface Action {
  /** The words. Also the row in the action sheet, so keep it a short verb phrase. */
  label: ReactNode;
  icon?: IconName;
  onClick?: () => void;
  /** A destination inside the app: rendered as a router Link. */
  to?: string;
  /** An address outside the app: rendered as a plain anchor, opened in a new tab. */
  href?: string;
  disabled?: boolean;
  loading?: boolean;
  /** A toggle. Rendered with aria-pressed, so a screen reader hears its state. */
  pressed?: boolean;
  /** Red: a destructive action. The confirmation stays the caller's job. */
  danger?: boolean;
  /** The accessible name, when the visible label is not enough on its own ("Edit Narrative report"). */
  ariaLabel?: string;
  /** One line under the label in the sheet: why it is disabled, or what it will do. */
  hint?: ReactNode;
  /** Stable React key, needed when `label` is not a string. */
  key?: string;
}

function keyOf(a: Action, i: number): string {
  return a.key ?? (typeof a.label === 'string' ? a.label : String(i));
}

/** An Action as a Button, a ButtonLink or an anchor, in the given variant. */
export function ActionButton({
  action,
  variant = 'ghost',
  size = 'sm',
  block,
  className,
}: {
  action: Action;
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
}) {
  const v: ButtonVariant = action.danger && variant === 'ghost' ? 'ghost' : variant;
  const cls = cx(action.danger && variant === 'ghost' && 'text-danger', className);
  if (action.to && !action.disabled) {
    return (
      <ButtonLink to={action.to} variant={v} size={size} icon={action.icon} block={block} className={cls} aria-label={action.ariaLabel}>
        {action.label}
      </ButtonLink>
    );
  }
  if (action.href && !action.disabled) {
    return (
      <a
        href={action.href}
        target="_blank"
        rel="noreferrer"
        aria-label={action.ariaLabel}
        className={cx(
          'inline-flex min-h-tap items-center justify-center gap-1.5 rounded-ct-sm px-3 py-1.5 text-xs font-semibold',
          // One colour, chosen here: two colour classes on one element are
          // settled by stylesheet order, and the green would win.
          action.danger ? 'text-danger' : 'text-forest',
          'hover:bg-forest-faint active:bg-forest-faint',
          focusRing,
          block && 'w-full',
          className,
        )}
      >
        {action.icon ? <Icon name={action.icon} size={16} /> : null}
        <span className="min-w-0 text-left">{action.label}</span>
      </a>
    );
  }
  return (
    <Button
      variant={v}
      size={size}
      icon={action.icon}
      block={block}
      className={cls}
      onClick={action.onClick}
      disabled={action.disabled}
      loading={action.loading}
      aria-pressed={action.pressed}
      aria-label={action.ariaLabel}
    >
      {action.label}
    </Button>
  );
}

/** Several actions side by side, for a desk. Ghost unless told otherwise. */
export function ActionRow({ actions, className }: { actions: Action[]; className?: string }) {
  return (
    <div className={cx('flex min-w-0 flex-wrap items-center gap-1', className)}>
      {actions.map((a, i) => (
        <ActionButton key={keyOf(a, i)} action={a} />
      ))}
    </div>
  );
}

export interface ActionSheetProps {
  open: boolean;
  onClose: () => void;
  /** The sheet's heading. "More actions", or the name of the record the actions belong to. */
  title: ReactNode;
  actions: Action[];
}

/**
 * The phone's overflow menu: a bottom sheet (the Dialog's phone form) of
 * 48px rows, icon then label, one per action. Choosing a row closes the
 * sheet first and then runs the action, so an action that opens its own
 * dialog gets the focus, and that dialog hands focus back to the button
 * that opened the sheet when it closes.
 */
export function ActionSheet({ open, onClose, title, actions }: ActionSheetProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} size="sm" flush>
      <ul className="divide-y divide-line">
        {actions.map((a, i) => (
          <li key={keyOf(a, i)}>
            <ActionSheetRow action={a} onDone={onClose} />
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

const sheetRow =
  'flex min-h-12 w-full items-center gap-3 px-5 py-3 text-left text-sm font-medium transition-colors duration-state ease-ct hover:bg-forest-whisper active:bg-forest-whisper disabled:cursor-not-allowed disabled:opacity-50';

/** One 48px row of a sheet. Exported so the shell's More sheet uses the same row. */
export function ActionSheetRow({ action, onDone }: { action: Action; onDone?: () => void }) {
  const tone = action.danger ? 'text-danger' : 'text-ink';
  const body = (
    <>
      {action.loading ? (
        <Spinner size={18} />
      ) : action.icon ? (
        <Icon name={action.icon} size={18} className={action.danger ? '' : 'text-forest'} />
      ) : (
        <span aria-hidden="true" className="w-[18px] shrink-0" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block">{action.label}</span>
        {action.hint ? <span className="block text-xs font-normal text-ink-3">{action.hint}</span> : null}
      </span>
      {action.pressed !== undefined ? (
        <Icon name="check" size={16} className={action.pressed ? 'text-forest' : 'invisible'} />
      ) : null}
    </>
  );
  if (action.to && !action.disabled) {
    return (
      <Link to={action.to} onClick={onDone} aria-label={action.ariaLabel} className={cx(sheetRow, tone, focusRing)}>
        {body}
      </Link>
    );
  }
  if (action.href && !action.disabled) {
    return (
      <a href={action.href} target="_blank" rel="noreferrer" onClick={onDone} aria-label={action.ariaLabel} className={cx(sheetRow, tone, focusRing)}>
        {body}
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled={action.disabled || action.loading}
      aria-pressed={action.pressed}
      aria-label={action.ariaLabel}
      onClick={() => {
        // Close first, in the same event, so the two state changes land in
        // one render: the sheet's focus hand-back runs before whatever the
        // action opens takes the focus for itself.
        onDone?.();
        action.onClick?.();
      }}
      className={cx(sheetRow, tone, focusRing)}
    >
      {body}
    </button>
  );
}

/**
 * The trailing "More actions" control: a 44px IconButton with three dots
 * (not the three rules of the bar's More, which is somewhere to go, not
 * something to do) that opens an ActionSheet. Use it when two or more actions would otherwise
 * share a line with a title on a phone.
 */
export function ActionMenuButton({
  actions,
  label = 'More actions',
  title,
  className,
}: {
  actions: Action[];
  /** The button's accessible name. Name the record when there are several on a screen: "Actions for Narrative report". */
  label?: string;
  /** The sheet heading. Defaults to the label. */
  title?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (actions.length === 0) return null;
  return (
    <>
      <IconButton
        icon="more"
        label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={className}
      />
      <ActionSheet open={open} onClose={() => setOpen(false)} title={title ?? label} actions={actions} />
    </>
  );
}
